/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api/inboundRouteApi.ts
 🗓️ Created: 2025-01-17
 🧩 Description:
    API functions for Inbound Routes.
    Centralizes all API calls related to Inbound Routes.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import {
  InboundRoute,
  InboundRoutePayload,
  ListInboundRoutesResponse,
  InboundRouteResponse
} from '../types';

export const INBOUND_ROUTES_ENDPOINT = '/api/telephony/inbound-routes';

/**
 * Generic API request helper
 */
async function requestInboundRoutesApi<T>(
  endpoint: string,
  errorMessage: string,
  options?: RequestInit
): Promise<{ data: T }> {
  const response = await fetch(buildApiUrl(endpoint), {
    ...options,
    headers: {
      ...getApiHeaders(),
      ...(options?.headers || {})
    }
  });

  if (!response.ok) {
    let message = errorMessage;
    try {
      const errorData = await response.json();
      if (errorData?.message) {
        message = errorData.message;
      }
    } catch {
      // Ignore JSON parse errors
    }
    throw new Error(message);
  }

  const result = await response.json();
  if (!result?.success) {
    throw new Error(result?.message || errorMessage);
  }

  return { data: result.data };
}

/**
 * List inbound routes with pagination and search
 */
export async function fetchInboundRoutes(
  pagination: {
    page: number;
    limit: number;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    domainId?: number;
  },
  search?: string,
  sortBy?: string
): Promise<ListInboundRoutesResponse> {
  const queryParams = new URLSearchParams({
    page: pagination.page.toString(),
    limit: pagination.limit.toString(),
    sortBy: sortBy || pagination.sortBy,
    sortOrder: pagination.sortOrder
  });

  if (search) {
    queryParams.append('search', search);
  }

  if (pagination.domainId != null && !Number.isNaN(Number(pagination.domainId))) {
    queryParams.append('domainId', pagination.domainId.toString());
  }

  const response = await fetch(
    buildApiUrl(`${INBOUND_ROUTES_ENDPOINT}?${queryParams}`),
    {
      headers: getApiHeaders()
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch inbound routes');
  }

  const result = await response.json();

  if (!result?.success || !Array.isArray(result.data)) {
    throw new Error(result?.message || 'Invalid response');
  }

  return {
    success: true,
    data: result.data as InboundRoute[],
    pagination: {
      page: result.pagination?.page || pagination.page,
      limit: result.pagination?.limit || pagination.limit,
      total: result.pagination?.total || 0,
      totalPages: result.pagination?.totalPages || 0
    }
  };
}

/**
 * Get single inbound route by ID
 */
export async function fetchInboundRoute(id: string | number): Promise<InboundRoute> {
  const { data } = await requestInboundRoutesApi<InboundRoute>(
    `${INBOUND_ROUTES_ENDPOINT}/${id}`,
    'Failed to fetch inbound route'
  );
  return data;
}

/**
 * Create new inbound route
 */
export async function createInboundRoute(
  payload: InboundRoutePayload
): Promise<InboundRoute> {
  const { data } = await requestInboundRoutesApi<InboundRoute>(
    INBOUND_ROUTES_ENDPOINT,
    'Failed to create inbound route',
    {
      method: 'POST',
      body: JSON.stringify(payload)
    }
  );
  return data;
}

/**
 * Update inbound route
 */
export async function updateInboundRoute(
  id: string | number,
  payload: InboundRoutePayload
): Promise<InboundRoute> {
  const { data } = await requestInboundRoutesApi<InboundRoute>(
    `${INBOUND_ROUTES_ENDPOINT}/${id}`,
    'Failed to update inbound route',
    {
      method: 'PUT',
      body: JSON.stringify(payload)
    }
  );
  return data;
}

/**
 * Delete inbound route
 */
export async function deleteInboundRoute(id: string | number): Promise<void> {
  await requestInboundRoutesApi<null>(
    `${INBOUND_ROUTES_ENDPOINT}/${id}`,
    'Failed to delete inbound route',
    {
      method: 'DELETE'
    }
  );
}

